import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/app/common/types/common';
import customFetchBase from '@/app/common/utils/customFetchBase';
import {
  DeleteHistoryPlannerRequest,
  ExportTableRequest,
  GetHistoryPlannerRequest,
  GetKeywordsByIdRequest,
  GetKeywordsPlannerRequest,
  SearchVolumeTrendRequest,
  SearchVolumeTrendResponse,
  StartPlannerRequest,
  StartPlannerResponse,
  PlannerDetailsData,
  PlannerHistoryItem,
  ClusterFromTaskRequest,
  ClusterFromTaskResponse
} from '@/app/KeywordResearch/types/googleAdsApiTypes';
import { getBaseApiPath } from '@/app/common/api/config';


const keywordPlannerApi = createApi({
  reducerPath: 'keywordPlannerApi',
  tagTypes: ['SearchVolumeChart', 'PlannerHistory', 'PlannerDetails'],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    getSearchVolumeDetailsChart: build.query<
      SearchVolumeTrendResponse,
      SearchVolumeTrendRequest
    >({
      query: (body: SearchVolumeTrendRequest) => ({
        url: '/google_ads/v2/keyword_planner/keyword_search_volumes_trend',
        method: 'POST',
        body
      }),
      providesTags: ['SearchVolumeChart']
    }),
    getHistoryPlanner: build.query<
      ApiResponse<PlannerHistoryItem[]>,
      GetHistoryPlannerRequest
    >({
      query: (body: GetHistoryPlannerRequest) => ({
        url: '/google_ads/v2/keyword_planner/history',
        method: 'POST',
        body
      }),
      providesTags: ['PlannerHistory']
    }),
    getKeywordsPlanner: build.query<
      ApiResponse<PlannerDetailsData>,
      GetKeywordsPlannerRequest
    >({
      query: (body: GetKeywordsPlannerRequest) => ({
        url: '/google_ads/v2/keyword_planner/get_by_id',
        method: 'POST',
        body
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'PlannerDetails', id: arg.task_id }
      ]
    }),
    deleteHistoryPlanner: build.mutation<
      ApiResponse<unknown>,
      DeleteHistoryPlannerRequest
    >({
      query: (body: DeleteHistoryPlannerRequest) => ({
        url: '/google_ads/v2/keyword_planner/delete',
        method: 'POST',
        body
      })
    }),
    getKeywordsPlannerFunc: build.mutation<
      ApiResponse<unknown>,
      GetKeywordsByIdRequest
    >({
      query: (body: GetKeywordsByIdRequest) => ({
        url: '/google_ads/v2/keyword_planner/keywords/get_by_id',
        method: 'POST',
        body
      })
    }),
    startPlanner: build.mutation<StartPlannerResponse, StartPlannerRequest>({
      query: (body: StartPlannerRequest) => ({
        url: '/google_ads/v3/keyword_planner/send_request',
        method: 'POST',
        body
      })
    }),
    exportTable: build.mutation<ApiResponse<unknown>, ExportTableRequest>({
      query: (body: ExportTableRequest) => ({
        url: '/google_ads/v2/export',
        method: 'POST',
        body
      })
    }),
    clusterPlannerFromTask: build.mutation<
      ClusterFromTaskResponse,
      ClusterFromTaskRequest
    >({
      query: (body: ClusterFromTaskRequest) => ({
        url: '/google_ads/v3/keyword_planner/cluster',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useGetHistoryPlannerQuery,
  useGetKeywordsPlannerFuncMutation,
  useGetKeywordsPlannerQuery,
  useStartPlannerMutation,
  useExportTableMutation,
  useLazyGetSearchVolumeDetailsChartQuery,
  useDeleteHistoryPlannerMutation,
  useClusterPlannerFromTaskMutation
} = keywordPlannerApi;

export default keywordPlannerApi;
